import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import Sidebar from '../../components/global/sideBar';
import Header from '../../components/provider/header';
import JobList from '../../components/provider/JobList';
import PostJobComponent from './createJob';
import Dashboard from '../../components/provider/AnalyticCard';
import MyProfile from './MyProfile';
import Settings from '../../components/global/settings';
import JobManagement from '../../components/provider/jobListTable'; // Import the JobManagement component
import HistoryTable from '../../components/provider/history'; // Import the HistoryTable component
import PrivacyPolicy from '../../components/provider/privacyPolicy'; // Import the PrivacyPolicy component
import ViewJob from '../../components/provider/viewJob'; // Import the ViewJob component
import ApplicantProfile from '../../components/provider/applicantProfile';
import DocumentsComponent from '../../components/provider/documents';
import GetHelpPage from '../../components/provider/getHelp';
import { ChakraProvider } from '@chakra-ui/react';
import theme from '../../config/themeProvider';
import Chat from '../../components/global/chat';
import Payment from '../../components/provider/payment';

const ProviderHome = () => {
  const [selectedView, setSelectedView] = useState('home');
  const [profileSubView, setProfileSubView] = useState('personalInfo');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const ticketToken = sessionStorage.getItem('ticketToken');
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const [homePageData, setHomePageData] = React.useState({
    avatarSrc: '',
    fullName: '',
    userName: '',
    licenseId: '',
    password: '',
    email: '',
    contactNumber: '',
    bio: '',
    address: '',
    specialties: '',
    services: '',
    organizationType: '',
    accreditations: '',
    role: '',
    countryCode: '',
    street: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
    individualIdentity: '',
    agencyDetails: {
      licenseId: '',
      certification: '',
      certificationExpiry: '',
      issuingAuthority: ''
    },
    organizationalSpecialization: ''
  });
  React.useEffect(() => {
    const fetchHomePageData = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        window.location.href = '/'; // Redirect to the root
        return;
      }
      // console.log('Ticket Token:', ticketToken);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/provider/get-homepage-details`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Response data:', response.data); // Log the response data

        // Update to match the response structure
        setHomePageData({
          fullName: response.data.data.FullName || '',
          email: response.data.data.email || '',
          role: response.data.data.role || '',
          countryCode: response.data.data.country_code || '',
          street: response.data.data.address?.street || '',
          city: response.data.data.address?.city || '',
          state: response.data.data.address?.state || '',
          country: response.data.data.address?.country || '',
          individualIdentity: response.data.data.individual_identity || '',
          agencyDetails: {
            licenseId: response.data.data.agencyDetails?.licenseId || '',
            certification: response.data.data.agencyDetails?.certification || '',
            certificationExpiry: response.data.data.agencyDetails?.certificationExpiry || '',
            issuingAuthority: response.data.data.agencyDetails?.issuingAuthority || ''
          },
          organizationType: response.data.data.organization_type || '',
          organizationalSpecialization: response.data.data.organizational_specialization || '',
          bio: response.data.data.bio || ''
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchHomePageData();
  }, []);
  useEffect(() => {
    if (selectedView === 'profile') {
      setProfileSubView('personalInfo');
    }
  }, [selectedView]);
  

  const navigateToHome = () => {
    setSelectedJob(null);
    setSelectedApplicant(null);
    setSelectedView('home');
  };

  const navigateToProfile = () => {
    setSelectedView('profile');
    setProfileSubView('personalInfo');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleOverlayClick = (e) => {
    if (isCompact && e.target.id === 'overlay') {
      setIsSidebarOpen(false);
    }
  };
  const handleSelectItem = (view) => {
    // Reset job and applicant when switching views
    setSelectedJob(null);
    setSelectedApplicant(null);
    setSelectedView(view);
  };
  const handleChatClick = (jobId) => {
    console.log("Navigating to chat with job ID:", jobId);
    // Logic to navigate to \global\chat.js, for example:
    setSelectedJob(jobId);
    setSelectedView("chat"); // Update the selected view if needed
  };
  const renderContent = () => {
    if (selectedView === "chat") {
      return <Chat jobId={selectedJob} />; // Pass jobId to the Chat component
    }
    if (selectedApplicant) {
      return <ApplicantProfile userId={selectedApplicant} jobId={selectedJob} onBack={() => setSelectedApplicant(null)} />; // Pass applicant and onBack function
    }

    if (selectedJob) {
      return (
        <ViewJob 
          jobId={selectedJob} 
          onBack={() => setSelectedJob(null)} 
          onViewApplicant={setSelectedApplicant} 
          onChatClick={handleChatClick}
          fromHistory={selectedView === 'history'} // Pass the prop
        />
      );
    }

    switch (selectedView) {
      case 'home':
        return (
          <>
            <Box flexShrink={0} width="100%" mb={4}>
              <PostJobComponent />
            </Box>
            <Box
              flex="1"
              display="flex"
              flexDirection={isCompact ? "column" : "row"} // Adjust flexDirection based on isCompact
              width="100%"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box
                flex="1"
                width={isCompact ? "100%" : "auto"} // Set width to 100% in compact mode
                height="80%"
                maxHeight="65vh"
                overflowY="auto"
                p={2}
                bg="white"
                boxShadow="md"
                borderRadius="lg"
                mt={2}
              >
                <JobList onViewJob={setSelectedJob} onViewApplicant={setSelectedApplicant} />
              </Box>
              <Box
                flex="1"
                width={isCompact ? "100%" : "auto"} // Set width to 100% in compact mode
                ml={{ base: 0, md: 4 }}  // Adjust margin between components for different screen sizes
                height="80%"
                maxHeight="65vh"
                overflowY="auto"
                p={2}
                bg="white"
                boxShadow="md"
                borderRadius="lg"
                mt={2}
              >
                <Dashboard />
              </Box>
            </Box>
          </>
        );
      // case 'profile':
      //   return <MyProfile selectedSection={profileSubView} />;
      case 'profile':
        switch (profileSubView) {
          case 'personalInfo':
            return <MyProfile initialFullName={homePageData.fullName || ''} initialEmail={homePageData.email || ''} initialRole={homePageData.role || ''} />;
          case 'documents':
            return <DocumentsComponent onBack={navigateToProfile} />; // Pass the onBack function
          case 'privacy':
            return (
              <Box
                height="100%"
                maxHeight="calc(100vh - 60px)" // Adjust based on your header height
                overflowY="auto" // Enable scrolling for the privacy policy section
                p={4}
                bg="white"
                boxShadow="md"
                borderRadius="lg"
                mt={2}
              >
                <PrivacyPolicy />
              </Box>
            );
          default:
            return null;
        }
  
      case 'settings':
        return <Settings />;
      case 'jobManagement': // Add a new case for jobManagement
        return (
          <Box
            overflowX="auto" // Enable horizontal scrolling
            width="100%" // Ensure it takes full width
          >
            <JobManagement onViewJob={setSelectedJob} onViewApplicant={setSelectedApplicant} /> 
          </Box>
        );
      case 'history': // Add a new case for history
        return (
          <Box
            flex="1" // Ensure it takes the available space
            overflowX="auto" // Enable horizontal scrolling
            width="100%" // Ensure it takes full width
          >
            <HistoryTable onViewJob={setSelectedJob} onViewApplicant={setSelectedApplicant} />
          </Box>
        );
        case 'help':
          return <GetHelpPage />;
      case 'payment':
        return (
          <Box
            flex="1" // Ensure it takes the available space
            overflowX="auto" // Enable horizontal scrolling
            width="100%" // Ensure it takes full width
          >
            <Payment />
          </Box>
        );
      default:
        return null;
    }
  };

  // Determine if the sidebar should be open based on screen size
  const isCompact = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    setIsSidebarOpen(!isCompact);
  }, [isCompact]);

  return (
    <ChakraProvider theme={theme}>
      <Flex height="100vh" overflow="hidden" position="relative" direction="column">
        <Box
          position="fixed"
          top="0"
          left={isSidebarOpen && !isCompact ? "250px" : "0"} // Adjust the left position
          right="0"
          zIndex="sticky"
          // transition="left 0.3s ease" // Add transition for smooth movement
        >
          <Header
            height="60px"
            userName={homePageData.fullName || ''}
            userEmail={homePageData.email || ''}
            onHomeClick={navigateToHome}
            onMenuClick={toggleSidebar}
            onProfileClick={() => setSelectedView('profile')}
          />
        </Box>
        <Flex flex="1" mt="60px" overflow="hidden">
          {isSidebarOpen && (
            <>
              <Box
                position={isCompact ? "absolute" : "fixed"}
                top="0"
                left="0"
                width="250px"
                height="100vh"
                bg="white"
                zIndex="overlay"
                boxShadow="lg"
              >
                <Sidebar
                  onSelectItem={handleSelectItem}
                  selectedItem={selectedView}
                  onSelectProfileSubView={setProfileSubView}
                />
              </Box>
              {isCompact && (
                <Box
                  id="overlay"
                  position="absolute"
                  top="0"
                  left="250px"
                  width="calc(100vw - 250px)"
                  height="100vh"
                  bg="rgba(0, 0, 0, 0.5)"
                  zIndex="overlay"
                  onClick={handleOverlayClick}
                />
              )}
            </>
          )}
          <Box
            flex="1"
            ml={isSidebarOpen && !isCompact ? "250px" : "0"}
            display="flex"
            flexDirection="column"
            height="100%"
            width={`calc(100% - ${isSidebarOpen && !isCompact ? "250px" : "0"})`}
            overflowX="auto" // Enable horizontal scrolling for the main content area
          >
            <Box flex="1" p={4} overflow="auto" display="flex" flexDirection="column" width="100%">
              {renderContent()}
            </Box>
          </Box>
        </Flex>
      </Flex>
    </ChakraProvider>
  );
};

export default ProviderHome;